import type {AuditMetricLabels, AuditTelemetryApi} from '@qep/preloads/src/preloads/QEP/audit-types';

type FlowLabels = AuditMetricLabels & {
  flow: string;
  stage: string;
  reason?: string;
};

type FlowMetricOptions = {
  error?: string;
  labels?: AuditMetricLabels;
};

const getRuntimeAudit = (): AuditTelemetryApi | undefined => (globalThis as typeof globalThis & {
  QEP?: {
    audit?: AuditTelemetryApi;
  };
}).QEP?.audit;

const buildFlowLabels = (flow: string, stage: string, labels?: AuditMetricLabels): FlowLabels => {
  if (!labels) {
    return {
      flow,
      stage,
    };
  }

  return {
    ...labels,
    flow,
    stage,
  };
};

export const beginFlowStage = (flow: string, stage: string, labels?: AuditMetricLabels): string | undefined => {
  const audit = getRuntimeAudit();
  if (!audit) {
    return undefined;
  }

  return audit.begin('flows', `${flow}.${stage}`, 'latencyMs', buildFlowLabels(flow, stage, labels));
};

export const endFlowStage = (
  token: string | undefined,
  flow: string,
  stage: string,
  options?: FlowMetricOptions,
): number | undefined => {
  const audit = getRuntimeAudit();
  if (!audit || !token) {
    return undefined;
  }

  return audit.end(token, {
    error: options?.error,
    labels: buildFlowLabels(flow, stage, options?.labels),
  });
};

export const recordFlowCounter = (
  flow: string,
  stage: string,
  metric: string,
  amount = 1,
  labels?: AuditMetricLabels,
): void => {
  getRuntimeAudit()?.increment('flows', `${flow}.${stage}`, metric, amount, buildFlowLabels(flow, stage, labels));
};

export const recordFlowTimer = (
  flow: string,
  stage: string,
  durationMs: number,
  labels?: AuditMetricLabels,
): void => {
  getRuntimeAudit()?.record('flows', `${flow}.${stage}`, 'latencyMs', durationMs, buildFlowLabels(flow, stage, labels));
};

export const recordFlowGauge = (
  flow: string,
  stage: string,
  metric: string,
  value: number | string | boolean | undefined,
  labels?: AuditMetricLabels,
): void => {
  getRuntimeAudit()?.gauge('flows', `${flow}.${stage}`, metric, value, buildFlowLabels(flow, stage, labels));
};

export const recordFlowError = (
  flow: string,
  stage: string,
  error: string,
  labels?: AuditMetricLabels,
): void => {
  getRuntimeAudit()?.setError('flows', `${flow}.${stage}`, error, buildFlowLabels(flow, stage, labels));
};
